/// <reference types="vite/client" />
import * as Sentry from '@sentry/react';
import { appEnv } from './app-env';

/**
 * Frontend Sentry — additive + inert unless a DSN is configured (mirrors
 * lib/analytics.ts). Config comes from the runtime-injected `window.__APP_ENV__`
 * first (served by `GET /v2/env.js` — the Nixpacks build bakes no VITE_* vars,
 * so build-time env is dead in prod), with the `import.meta.env.VITE_*`
 * build-time values as the local-dev fallback. No DSN → init is skipped and the
 * ErrorBoundary just renders children / shows a fallback on a render crash,
 * with no network. sendDefaultPii is false (no-PII contract, matching the
 * backend + analytics). Fail-open.
 */
export function initSentry(): void {
  const env = appEnv();
  const dsn = env.sentryDsn || import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;
  try {
    Sentry.init({
      dsn,
      environment: env.sentryEnvironment || import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
      release: env.release || import.meta.env.VITE_RELEASE || undefined,
      tracesSampleRate: Number(env.sentryTracesSampleRate ?? import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0),
      sendDefaultPii: false,
      integrations: [Sentry.browserTracingIntegration()],
    });
  } catch {
    /* fail-open — a telemetry init must never break app boot */
  }
}

// Re-export so main.tsx imports the boundary from one place.
export const SentryErrorBoundary = Sentry.ErrorBoundary;
